// The Documents page's words and dates (design_handoff_founder_assistant screen 3; backend/documents.py), worked out
// from each document's record in the browser's time. Every date and weekday comes from the data: the handoff's
// samples follow the 2025 calendar. backend/tests/test_documents_js.py runs this file under Node.

export const TYPES = [["investor_update", "Investor update"], ["weekly_report", "Weekly founder report"],
  ["money_sheet", "Monthly money sheet"], ["objective_brief", "Objective brief"]];
export const TYPE_LABEL = Object.fromEntries(TYPES);
export const FORMAT_LABEL = { email_pdf: "Email and PDF", pdf: "PDF" };
export const RULE_LABEL = { once: "once", every_monday: "every Monday", first_monday: "first Monday of the month" };
export const STATUSES = [["preparing", "Preparing"], ["ready", "Ready"], ["sent", "Sent"], ["scheduled", "Scheduled"],
  ["held", "Held: no credits"], ["nocheck", "Couldn't check the numbers"], ["failed", "Couldn't prepare it"]];
// the main action follows the status
export const MAIN_ACTION = { preparing: "Send", ready: "Send", sent: "Send again", scheduled: "Open schedule",
  held: "Add credits", nocheck: "Try again", failed: "Try again" };

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_LONG = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October",
  "November", "December"];
const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const pad = (n) => String(n).padStart(2, "0");
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

const dayMonth = (d) => `${d.getDate()} ${MONTHS[d.getMonth()]}`;
const clock = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;
const sameDay = (a, b) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
// a period's "2026-10-06" is a calendar date, never a moment: read without any time zone
const calendar = (s) => { const [y, m, d] = s.split("-").map(Number); return new Date(y, m - 1, d); };

// "Today, 10:14" or "17 Oct, 09:00"
export function stamp(iso, now = new Date()) {
  const d = new Date(iso);
  return `${sameDay(d, now) ? "Today" : dayMonth(d)}, ${clock(d)}`;
}

// "Mon 2 Nov, 09:00"
export function weekdayStamp(iso) {
  const d = new Date(iso);
  return `${WEEKDAYS[d.getDay()]} ${dayMonth(d)}, ${clock(d)}`;
}

// whole calendar days from today to that day, in the browser's time (a document prepared on 13 Oct is deleted 90 days
// later, on 11 Jan: on 17 Oct that's in 86 days, whatever the hour)
export function daysUntil(iso, now = new Date()) {
  const d = new Date(iso);
  return Math.round((Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()) - Date.UTC(now.getFullYear(), now.getMonth(), now.getDate())) / 86400000);
}

// "October 2026", "Q3 2026", "6 to 12 Oct" or "29 Sep to 5 Oct", "Due 14 Nov"
export function periodLabel(p) {
  if (!p?.start) return "—";
  const start = calendar(p.start);
  const end = p.end ? calendar(p.end) : start;
  if (p.kind === "month") return `${MONTHS_LONG[start.getMonth()]} ${start.getFullYear()}`;
  if (p.kind === "quarter") return `Q${Math.floor(start.getMonth() / 3) + 1} ${start.getFullYear()}`;
  if (p.kind === "due") return `Due ${dayMonth(start)}`;
  return start.getMonth() === end.getMonth() ? `${start.getDate()} to ${dayMonth(end)}` : `${dayMonth(start)} to ${dayMonth(end)}`;
}

export const docName = (doc) => TYPE_LABEL[doc.type] || doc.type;

// under the name: its format, then the objective a brief is for, or how a scheduled or held update repeats
export function docSub(doc) {
  const detail = doc.type === "objective_brief" ? doc.objective_title
    : ["scheduled", "held"].includes(doc.status) ? RULE_LABEL[doc.rule] : null;
  return [FORMAT_LABEL[doc.format], detail].filter(Boolean).join(" · ");
}

// the status word: "Sent to 2 people", "Scheduled · Mon 2 Nov, 09:00"
export function statusWord(doc) {
  if (doc.status === "sent") return `Sent to ${plural(doc.sent_count || 0, "person", "people")}`;
  if (doc.status === "scheduled") {
    if (!doc.scheduled_for) return "Scheduled";
    return `Scheduled · ${doc.tz ? zonedStamp(doc.scheduled_for, doc.tz) : weekdayStamp(doc.scheduled_for)}`;
  }
  return Object.fromEntries(STATUSES)[doc.status] || doc.status;
}

// PREPARED: when it was prepared, when a preparing one started, when a held one was due; nothing for a scheduled one
export function preparedLabel(doc, now = new Date()) {
  if (doc.status === "scheduled") return "—";
  const at = doc.status === "held" ? doc.held?.due_at : doc.status === "preparing" ? doc.created_at : doc.prepared_at;
  return at ? stamp(at, now) : "—";
}

// KEPT: "Deleted in 86 days"; a held update "Dropped on 24 Oct" (a warning); nothing while it's preparing or scheduled
export function keptLabel(doc, now = new Date()) {
  if (doc.status === "held") return { text: doc.held?.drop_at ? `Dropped on ${dayMonth(new Date(doc.held.drop_at))}` : "—", warn: true };
  if (["preparing", "scheduled", "failed"].includes(doc.status) || !doc.expires_at) return { text: "—" };
  const days = daysUntil(doc.expires_at, now);
  return { text: days <= 0 ? "Deleted today" : `Deleted in ${plural(days, "day", "days")}` };
}

// what each status allows: preparing allows only Delete; a scheduled update isn't prepared yet, so has no preview;
// held, scheduled and couldn't-check allow no download or
// copy; a PDF-only document has no email to copy
export function allowed(doc) {
  const files = doc.files || {};
  const blocked = ["preparing", "held", "scheduled", "nocheck", "failed"].includes(doc.status);
  return { preview: !["preparing", "scheduled"].includes(doc.status), main: doc.status !== "preparing",
           pdf: !blocked && !!files.pdf, copy: !blocked && doc.format !== "pdf" && !!files.email };
}

// the page's status sentence: "4 documents ready, 1 investor update scheduled for Mon 2 Nov, 09:00."
export function headline(docs) {
  if (!docs.length) return "Nothing prepared yet.";
  const ready = docs.filter((d) => d.status === "ready").length;
  const scheduled = docs.filter((d) => d.status === "scheduled" && d.scheduled_for)
    .sort((a, b) => a.scheduled_for.localeCompare(b.scheduled_for));
  const parts = [];
  if (ready) parts.push(`${plural(ready, "document", "documents")} ready`);
  if (scheduled.length) parts.push(`${plural(scheduled.length, "investor update", "investor updates")} scheduled for ${weekdayStamp(scheduled[0].scheduled_for)}`);
  if (!parts.length) return `${plural(docs.length, "document", "documents")}, none ready to send.`;
  const sentence = parts.join(", ");
  return `${sentence[0].toUpperCase()}${sentence.slice(1)}.`;
}

// ---------------- the preview page (design_handoff_founder_assistant screen 4) ----------------
const MONTH_OF = Object.fromEntries(MONTHS.map((m, i) => [m, MONTHS_LONG[i]]));

// "Investor update, October 2026." / "Weekly founder report, 6 to 12 October." / "Objective brief, <title>."
export function previewTitle(doc) {
  const what = doc.type === "objective_brief" ? doc.objective_title
    : periodLabel(doc.period).replace(/\b(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\b/g, (m) => MONTH_OF[m]);
  return `${docName(doc)}${what ? `, ${what}` : ""}.`;
}

// the header's status sentence; a held update's follows whether credits came (`creditsOk`)
export function previewLine(doc, creditsOk = false) {
  if (doc.status === "held") return creditsOk ? "Credits added. Send it now or skip it." : `Held: no credits. ${dropLine(doc)}`.trim();
  if (doc.status === "ready") return "Ready to send. Every number checked.";
  if (doc.status === "nocheck") return "Couldn't check the numbers, so it can't be sent.";
  if (doc.status === "failed") return "Couldn't prepare it. Nothing was sent.";
  if (doc.status === "preparing") return "Being prepared. It shows here when it's ready.";
  return `${statusWord(doc)}.`;
}

// the number check's line: "Every number checked against your Crewbench data: 38 numbers." or "38 of 41 numbers match
// your Crewbench data."
export function checkLine(check) {
  if (!check) return "";
  return check.matching === check.count
    ? `Every number checked against your Crewbench data: ${plural(check.count, "number", "numbers")}.`
    : `${check.matching} of ${plural(check.count, "number", "numbers")} match your Crewbench data.`;
}

// Sources: the ones whose numbers don't match first ("3 DON'T MATCH"), then each with its matching count
export function sourceRows(check) {
  const rows = [];
  for (const s of check?.sources || []) {
    if (s.mismatches) rows.push({ ...s, count: s.mismatches, kind: `${s.mismatches} DON'T MATCH`, bad: true });
  }
  for (const s of check?.sources || []) {
    if (s.count - (s.mismatches || 0) > 0) rows.push({ ...s, count: s.count - (s.mismatches || 0), kind: "CREWBENCH PAGE" });
  }
  return rows;
}

// "3 of 41 numbers don't match your Crewbench data: spend changed while the report was being prepared. Nothing can be
// sent until they match."
export function mismatchLine(check) {
  const bad = check.count - check.matching;
  const spend = (check.mismatches || []).every((m) => ["money", "budgets"].includes(m.source));
  return `${bad} of ${plural(check.count, "number", "numbers")} don't match your Crewbench data: `
    + `${spend ? "spend changed" : "they changed"} while the report was being prepared. Nothing can be sent until they match.`;
}

// why a document couldn't be prepared, in words (backend doc_jobs.py)
const PROBLEMS = {
  no_server: "The document server isn't connected right now.",
  credits: "The assistant runs on Crewbench credits. Add credits in the Store.",
  writer: "Its words had a number that isn't from your Crewbench data, twice.",
  timeout: "It took longer than 10 minutes.",
};
export const problemLine = (problem) => `${PROBLEMS[problem?.code] || "Something went wrong while preparing it."} Nothing was sent.`;

// "About $0.06 of credits."
export const credits = (usd) => `about $${Number(usd).toFixed(2)} of credits`;

// ---------------- the assistant's Prepare-a-document card (components/Assistant.jsx, screen 2) ----------------
// what each answer cost, rounded up to the cent: "$0.01 of credits"; nothing when it cost nothing
export function costLine(usd) {
  if (!(usd > 0)) return "";
  return `$${(Math.ceil(usd * 100 - 1e-9) / 100).toFixed(2)} of credits`;
}

// "Daniel Reyes", "Daniel Reyes and Priya Nair", "Daniel Reyes, Priya Nair and Omar"
export function names(list) {
  const n = list.map((r) => r.name || r.email).filter(Boolean);
  return n.length < 2 ? n.join("") : `${n.slice(0, -1).join(", ")} and ${n[n.length - 1]}`;
}

// "Investor update, October 2026" (the preview's title, without its full stop)
export const cardTitle = (card) => previewTitle(card).replace(/\.$/, "");

// after Prepare: "Email and PDF · for Daniel Reyes and Priya Nair. It waits in Documents for you to preview and send."
export function preparingLine(card) {
  const format = card.format === "pdf" ? "PDF only" : "Email and PDF";
  const who = card.recipients?.length ? ` · for ${names(card.recipients)}` : "";
  const then = card.format === "pdf" ? "preview and download" : "preview and send";
  return `${format}${who}. It waits in Documents for you to ${then}.`;
}

const iso = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

// the months a document can cover, newest first: this month, then the 11 before it
export function monthChoices(now = new Date(), count = 12) {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    return [iso(d).slice(0, 7), `${MONTHS_LONG[d.getMonth()]} ${d.getFullYear()}`];
  });
}

// a whole month as a period, up to today while it's this month
export function monthPeriod(month, now = new Date()) {
  const [y, m] = month.split("-").map(Number);
  const last = new Date(y, m, 0);
  const end = last > now ? now : last;
  return { kind: "month", start: `${month}-01`, end: iso(end) };
}

// Monday to Sunday: this week, or the last full one
export function weekPeriod(last, now = new Date()) {
  const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - ((now.getDay() + 6) % 7) - (last ? 7 : 0));
  const end = new Date(start.getFullYear(), start.getMonth(), start.getDate() + 6);
  return { kind: "week", start: iso(start), end: iso(end) };
}

// ticking a block: Hiring costs are the Fee column of the money table, so without Money per objective they go too
export function toggleBlock(blocks, key, on) {
  return blocks.map((b) => {
    if (b.key === key) return { ...b, on };
    if (key === "money" && b.key === "hiring_costs") return { ...b, on: on && b.on, needs: !on };
    return b;
  });
}

// the accepted card's row, from its document: its status word and tone, a line, and where its link goes
export function cardStatus(doc) {
  if (!doc) return { word: "Deleted", tone: "muted", line: "This document was deleted.", link: null };
  if (doc.status === "preparing") return { word: "Preparing…", tone: "plain", line: preparingLine(doc), link: "documents" };
  if (doc.status === "nocheck") {
    return { word: "Couldn't check the numbers", tone: "danger", link: "preview",
             line: doc.number_check ? mismatchLine(doc.number_check) : previewLine(doc) };
  }
  if (doc.status === "failed") return { word: "Couldn't prepare it", tone: "danger", line: problemLine(doc.problem), link: "preview" };
  return { word: statusWord(doc), tone: doc.status === "ready" ? "info" : "plain", line: checkLine(doc.number_check), link: "preview" };
}

// ---------------- the assistant's web and imported files (part 3b) ----------------
// each web page an answer's facts came from, once, in order: the box FROM THE WEB lists them
export function webSources(facts) {
  const seen = new Map();
  for (const f of facts || []) if (f.source === "web" && f.url && !seen.has(f.url)) seen.set(f.url, { url: f.url, title: f.title || f.url });
  return [...seen.values()];
}

// "example.com/guides/monthly-investor-update": a page's address without its scheme
export const address = (url) => String(url || "").replace(/^https?:\/\//, "").replace(/\/$/, "");

// what an imported file is, from its type or, for a CSV browsers type differently, its name; null when it can't be read
const IMPORTABLE = ["application/pdf", "text/csv", "image/png", "image/jpeg", "image/webp", "image/gif"];
export function importType(name, type) {
  if (IMPORTABLE.includes(type)) return type;
  const ext = String(name || "").toLowerCase().split(".").pop();
  return { csv: "text/csv", pdf: "application/pdf", png: "image/png", jpg: "image/jpeg", jpeg: "image/jpeg" }[ext] || null;
}

// the chip's line while a file is read: "Reading… page 3 of 8", or "Reading…" for one page
export function readingLine(file) {
  if (file.pages > 1) return `Reading… page ${Math.min(file.read + 1, file.pages)} of ${file.pages}`;
  return "Reading…";
}

// ---------------- the Send dialog (design screen 5) ----------------
export const LIMIT = 50;  // emails a day per company (backend doc_send.LIMIT)

// "Send the investor update"
export const sendTitle = (doc) => `Send the ${docName(doc).toLowerCase()}`;

// "October 2026 · Email and PDF"
export function sendSub(doc) {
  const what = doc.type === "objective_brief" ? doc.objective_title : periodLabel(doc.period);
  return [what, FORMAT_LABEL[doc.format]].filter((x) => x && x !== "—").join(" · ");
}

// "3 of 50 emails sent today"
export const todayLine = (n, limit = LIMIT) => `${n} of ${limit} emails sent today`;

// "This sends 2 emails"
export const sendsLine = (n) => `This sends ${plural(n, "email", "emails")}`;

// over the daily limit: what to do (backend doc_send.over_limit)
export function overLine(today, adding, limit = LIMIT) {
  const extra = today + adding - limit;
  return extra > 0 ? `This would go over today's limit of ${limit} emails. Remove ${plural(extra, "person", "people")}, or schedule it for tomorrow.` : "";
}

// after Send: "Daniel Reyes and Priya Nair · 10:21"
export const sentLine = (people, at) => `${names(people)} · ${clock(new Date(at))}`;

// ---------------- the investor update scheduler (design screen 6) ----------------
export const RULE_NAME = { once: "Once", every_monday: "Every Monday", first_monday: "First Monday of the month" };
const BLOCK_NAME = { progress: "Progress", money: "Money per objective", hiring_costs: "Hiring costs", plan: "Plan and credits",
  reliability: "Reliability", delivery: "Delivery times" };

// "Berlin time": the city of a time zone
export const zoneLabel = (tz) => `${String(tz || "UTC").split("/").pop().replace(/_/g, " ")} time`;

// the wall clock in a time zone: { date: "2026-10-22", time: "09:21" }
export function zonedNow(tz, now = new Date()) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: tz, year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(now).map((p) => [p.type, p.value]));
  return { date: `${parts.year}-${parts.month}-${parts.day}`, time: `${parts.hour}:${parts.minute}` };
}

const day = (s) => calendar(s);
const addDays = (s, n) => { const d = day(s); d.setDate(d.getDate() + n); return iso(d); };

// the schedule's next `n` dates (calendar days in its time zone), as the scheduler shows them before saving
export function nextDates(rule, start, time, tz, n = 3, now = new Date()) {
  if (!start || !time) return [];
  const here = zonedNow(tz, now);
  const coming = (d) => d > here.date || (d === here.date && time > here.time);
  if (rule === "once") return coming(start) ? [start] : [];
  const out = [];
  let d = addDays(start, (8 - day(start).getDay()) % 7);  // the first Monday from the start
  for (let i = 0; out.length < n && i < 600; i += 1, d = addDays(d, 7)) {
    if ((rule === "every_monday" || day(d).getDate() <= 7) && coming(d)) out.push(d);
  }
  return out;
}

// "Mon 3 Nov", or with its year "Mon 3 Nov 2026"
export const dayLabel = (s, year = false) => { const d = day(s); return `${WEEKDAYS[d.getDay()]} ${dayMonth(d)}${year ? ` ${d.getFullYear()}` : ""}`; };

// under the Next line: "Then Mon 1 Dec and Mon 5 Jan", "Then every Monday: 27 Oct, 3 Nov, 10 Nov", "Sent once. Nothing after that."
export function thenLine(rule, dates) {
  if (rule === "once") return "Sent once. Nothing after that.";
  const rest = dates.slice(1);
  if (!rest.length) return "";
  if (rule === "every_monday") return `Then every Monday: ${rest.map((d) => dayMonth(day(d))).join(", ")}`;
  return `Then ${names(rest.map((d) => ({ name: dayLabel(d) })))}`;
}

// a saved schedule's next time, from its UTC instant, in its own time zone: "Mon 3 Nov, 09:00"
export function zonedStamp(iso8601, tz) {
  const d = new Date(iso8601);
  const p = Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: tz, weekday: "short", day: "numeric", month: "short",
    hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).formatToParts(d).map((x) => [x.type, x.value]));
  return `${p.weekday} ${p.day} ${p.month}, ${p.hour}:${p.minute}`;
}

// the Scheduled tab's line: "First Monday of the month, 09:00 Berlin time · 6 blocks and your note"
export function ruleLine(s) {
  const blocks = (s.blocks || []).filter((b) => b !== "note");
  const what = blocks.length <= 2 ? names(blocks.map((b) => ({ name: BLOCK_NAME[b] || b }))) : `${blocks.length} blocks`;
  return `${RULE_NAME[s.rule]}, ${s.time} ${zoneLabel(s.tz)} · ${what}${s.note ? " and your note" : ""}`;
}

// the scheduler's header: "Next: Mon 3 Nov, 09:00, to Daniel Reyes and Priya Nair."
export function scheduleHeadline(date, time, people) {
  const to = people.length ? names(people) : "nobody yet";
  return date ? `Next: ${dayLabel(date)}, ${time}, to ${to}.` : "Pick a date and time that haven't passed.";
}

// ---------------- no credits (design screen 9) ----------------
// "-$1.30"
export const minus = (usd) => `-$${Number(usd).toFixed(2)}`;

// the banner on Documents and the Overview while a scheduled update that ran on credit is owed
export const owedLine = (usd) => `Your balance is ${minus(usd)}: the investor update ran on credit. Your next credits cover this first.`;

// "Dropped on 24 Oct if nothing is chosen."
export const dropLine = (doc) => (doc.held?.drop_at ? `Dropped on ${dayMonth(new Date(doc.held.drop_at))} if nothing is chosen.` : "");

// the held update's panel: before credits, and once they came
export function heldLine(doc, creditsOk) {
  if (creditsOk) {
    const to = doc.recipients?.length ? ` to ${names(doc.recipients)}` : "";
    return `Credits added. Send now prepares the numbers again, then sends it${to}.`;
  }
  return `This update was due on ${doc.held?.due_at ? weekdayStamp(doc.held.due_at) : "its date"}. Add credits, then send it or skip it.`;
}

// ---------------- the Create-an-objective card (part 5; design screen 2) ----------------
// "$40", "$1,580", "$12.50"
export const dollars = (n) => {
  const v = Number(n || 0);
  return `$${v.toLocaleString("en-US", Number.isInteger(v) ? {} : { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

// the card's footer: "Website & legal has $48 of $150 left this month"
export const fitLine = (project, fit) => (project && fit ? `${project.name} has ${dollars(fit.left_usd)} of ${dollars(fit.amount_usd)} left this month` : "");

// "Website & legal · $40 AI budget · due Fri 14 Nov"
export const objectiveFacts = (c) => [c.project?.name, c.budget_usd ? `${dollars(c.budget_usd)} AI budget` : "No AI budget",
  c.due_date && `due ${dayLabel(c.due_date)}`].filter(Boolean).join(" · ");

// the created card's line: an independent professional's objective starts once they agree to the hiring terms
export function createdLine(c) {
  const first = (c.person?.name || "").split(" ")[0];
  const after = !c.person ? "" : c.hire ? ` It starts when ${first} agrees to the hiring terms.` : ` ${first} is assigned.`;
  return `${objectiveFacts(c)}.${after}`;
}

// Fill in the hiring form: Team opens Hire for another task with the new objective and its budget
export const hireLink = (c) => `/founder/team?${new URLSearchParams({
  hire: c.person.id, objective: c.objective_id, ...(c.project ? { project: c.project.id } : {}), budget: String(c.budget_usd || ""),
})}`;
